import { useState, useEffect } from 'react';
import { Plus, Search, Phone, Mail, MapPin, Pencil, Trash2, UserX } from 'lucide-react';
import { getCustomers, createCustomer, updateCustomer, deleteCustomer } from '../api/customers';
import { Button } from '../components/ui/Button';
import { Input, Textarea } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { AddressPicker, buildFullAddress } from '../components/ui/AddressPicker';
import { MapPicker } from '../components/ui/MapPicker';

function CustomerForm({ customer, onSuccess, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    name:           customer?.name           || '',
    email:          customer?.email          || '',
    phone:          customer?.phone          || '',
    city:           customer?.city           || '',
    district:       customer?.district       || '',
    address_detail: customer?.address_detail || '',
    latitude:       customer?.latitude       || '',
    longitude:      customer?.longitude      || '',
    notes:          customer?.notes          || '',
  });

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setErrors({ name: 'Ad gerekli' }); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        address:   buildFullAddress({ city: form.city, district: form.district, address_detail: form.address_detail }),
        latitude:  form.latitude  ? parseFloat(form.latitude)  : undefined,
        longitude: form.longitude ? parseFloat(form.longitude) : undefined,
      };
      customer ? await updateCustomer(customer.id, payload) : await createCustomer(payload);
      onSuccess();
    } catch (err) {
      setErrors({ submit: err.response?.data?.error || 'Hata oluştu' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input label="Müşteri Adı *" value={form.name} onChange={set('name')} error={errors.name} placeholder="Firma veya şahıs adı" />
      <div className="grid grid-cols-2 gap-4">
        <Input label="Telefon" value={form.phone} onChange={set('phone')} placeholder="05xx xxx xx xx" />
        <Input label="E-posta" type="email" value={form.email} onChange={set('email')} placeholder="ornek@mail.com" />
      </div>
      <AddressPicker
        value={{ city: form.city, district: form.district, address_detail: form.address_detail }}
        onChange={(v) => {
          const cityChanged     = v.city           !== form.city;
          const districtChanged = v.district       !== form.district;
          const detailChanged   = v.address_detail !== form.address_detail;
          setForm(f => ({
            ...f,
            city: v.city, district: v.district, address_detail: v.address_detail,
            ...(cityChanged || districtChanged || detailChanged ? { latitude: '', longitude: '' } : {}),
          }));
        }}
      />
      <MapPicker
        geocodeHint={[form.address_detail, form.district, form.city].filter(Boolean).join(', ')}
        latitude={form.latitude}
        longitude={form.longitude}
        onChange={({ latitude, longitude }) => setForm(f => ({ ...f, latitude, longitude }))}
      />
      <Textarea label="Notlar" value={form.notes} onChange={set('notes')} rows={3} placeholder="Ek bilgiler..." />
      {errors.submit && <p className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg">{errors.submit}</p>}
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel}>İptal</Button>
        <Button type="submit" loading={loading}>{customer ? 'Güncelle' : 'Kaydet'}</Button>
      </div>
    </form>
  );
}

export function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const load = () => {
    setLoading(true);
    getCustomers().then(d => setCustomers(d.customers)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = customers.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.name.toLowerCase().includes(q) ||
      c.phone?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q);
  });

  const handleDelete = async (c) => {
    if (!window.confirm(`"${c.name}" müşterisini silmek istediğinizden emin misiniz?`)) return;
    try {
      await deleteCustomer(c.id);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Silinemedi');
    }
  };

  const openEdit = (c) => { setEditTarget(c); setFormOpen(true); };
  const closeForm = () => { setFormOpen(false); setEditTarget(null); };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Müşteriler</h1>
          <p className="text-slate-500 text-sm mt-0.5">{customers.length} müşteri</p>
        </div>
        <Button onClick={() => { setEditTarget(null); setFormOpen(true); }}>
          <Plus size={16} />
          Yeni Müşteri
        </Button>
      </div>

      {/* Arama */}
      <div className="relative mb-5">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="İsim, telefon veya e-posta ara..."
          className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Liste */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-36 bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-slate-300 text-5xl mb-3">🏢</div>
          <p className="text-slate-500">{search ? 'Eşleşen müşteri yok' : 'Henüz müşteri eklenmedi'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(c => (
            <div key={c.id} className={`bg-white rounded-xl border border-slate-200 p-4 flex flex-col gap-2 ${!c.is_active ? 'opacity-50' : ''}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm shrink-0">
                    {c.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 text-sm truncate">{c.name}</p>
                    {!c.is_active && (
                      <span className="text-xs text-slate-400 flex items-center gap-1"><UserX size={10} /> Pasif</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => openEdit(c)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition">
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => handleDelete(c)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>

              <div className="space-y-1 text-xs text-slate-500">
                {c.phone && (
                  <div className="flex items-center gap-1.5">
                    <Phone size={11} className="shrink-0" /><span>{c.phone}</span>
                  </div>
                )}
                {c.email && (
                  <div className="flex items-center gap-1.5">
                    <Mail size={11} className="shrink-0" /><span className="truncate">{c.email}</span>
                  </div>
                )}
                {c.address && (
                  <div className="flex items-start gap-1.5">
                    <MapPin size={11} className="shrink-0 mt-0.5" /><span className="line-clamp-2">{c.address}</span>
                  </div>
                )}
                {c.notes && (
                  <p className="text-slate-400 italic line-clamp-2 mt-1">{c.notes}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={formOpen}
        onClose={closeForm}
        title={editTarget ? 'Müşteriyi Düzenle' : 'Yeni Müşteri'}
      >
        <CustomerForm
          customer={editTarget}
          onSuccess={() => { closeForm(); load(); }}
          onCancel={closeForm}
        />
      </Modal>
    </div>
  );
}
